import { createContext, useContext, useEffect, useState } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import { setAuthToken } from "../utils/axiosInstance"; // 👈 import helper

const AuthContext = createContext();
const API_URL = process.env.REACT_APP_API_URL || "http://localhost:5000";

export const AuthProvider = ({ children }) => {
  const { isAuthenticated, isLoading, getAccessTokenSilently, logout } = useAuth0();
  const [appUser, setAppUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (isLoading) return;

    let mounted = true;

    const fetchAppUser = async () => {
      if (!isAuthenticated) {
        if (mounted) setAppUser(null);
        if (mounted) setLoading(false);
        setAuthToken(null); // ❌ clear axios token
        return;
      }

      try {
        // ✅ Get Auth0 API access token
        const token = await getAccessTokenSilently({
          audience: process.env.REACT_APP_AUTH0_AUDIENCE,
        });

        setAuthToken(token); // 🔑 set axios default header

        // ✅ Call backend for trusted user info
        const res = await fetch(`${API_URL}/api/me`, {
          headers: { Authorization: `Bearer ${token}` },
        });

        if (!res.ok) throw new Error("Failed to fetch user from backend");
        const data = await res.json();

        if (mounted) setAppUser(data);
      } catch (err) {
        console.error("AuthProvider error fetching user:", err);
        if (mounted) setAppUser(null);
        setAuthToken(null);

        // Optional: if token fails hard, log out user
        // logout({ returnTo: window.location.origin });
      }

      if (mounted) setLoading(false);
    };

    fetchAppUser();
    return () => { mounted = false };
  }, [isLoading, isAuthenticated, getAccessTokenSilently, logout]);

  return (
    <AuthContext.Provider value={{ appUser, loading }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAppAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error("useAppAuth must be used within an AuthProvider");
  return context;
};
